import { useEffect, useState } from "react";
import api, { formatCLP, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { Award, Package, User, Gift, Coins, Check, AlertCircle, Sparkles } from "lucide-react";

const STATUS_COLORS = {
    pendiente: "bg-mexyellow/30 text-cocoa",
    pagado: "bg-marigold/15 text-marigold",
    enviado: "bg-blue-100 text-blue-800",
    entregado: "bg-green-100 text-green-800",
    retirado: "bg-green-100 text-green-800",
    cancelado: "bg-muted text-muted-foreground",
    devolucion: "bg-orange-100 text-orange-800",
};

const STATUS_LABELS = {
    pendiente: "Pendiente",
    pagado: "Pagado",
    enviado: "Enviado",
    entregado: "Entregado",
    retirado: "Retirado",
    cancelado: "Cancelado",
    devolucion: "Devolución",
};

export default function Profile() {
    const { user, refresh } = useAuth();
    const { pendingReward, setPendingReward, setOpen } = useCart();
    const [orders, setOrders] = useState([]);
    const [rewards, setRewards] = useState([]);
    const [redeeming, setRedeeming] = useState(false);

    const loadRewards = () =>
        api.get("/rewards").then(({ data }) => setRewards(data)).catch(() => {});

    const loadOrders = () =>
        api.get("/orders/me").then(({ data }) => setOrders(data)).catch(() => {});

    useEffect(() => {
        loadOrders();
        loadRewards();
    }, []);

    if (!user) return null;

    const points = user.points || 0;
    const cashValue = points * 10; // 1 punto = $10 CLP

    const selectReward = (reward) => {
        if ((user.points || 0) < reward.points_cost) {
            toast.error("No tienes puntos suficientes para este canjeable");
            return;
        }
        setPendingReward({
            reward_id: reward.id,
            name: reward.name,
            points_cost: reward.points_cost,
            photo_base64: reward.photo_base64 || "",
        });
        toast.success(`"${reward.name}" agregado al canje. Haz un pedido de $10.000 CLP o más para completarlo.`);
        setOpen(true);
    };

    const redeemCash = async () => {
        if (user.cash_redeemed) {
            toast.error("Ya canjeaste tus puntos por dinero. Solo se permite 1 vez.");
            return;
        }
        if (points < 1) {
            toast.error("No tienes puntos disponibles");
            return;
        }
        const ok = window.confirm(
            `¿Canjear tus ${points} puntos por ${formatCLP(cashValue)}?\n\n` +
            `Este canje es por única vez. Después solo podrás usar tus puntos para canjear productos.\n\n` +
            `Nuestro equipo te contactará para coordinar el pago.`
        );
        if (!ok) return;
        setRedeeming(true);
        try {
            await api.post("/redeem/cash");
            toast.success(`¡Solicitud enviada! Te pagaremos ${formatCLP(cashValue)}.`);
            await refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setRedeeming(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="profile-page">
            {/* Header: user info + points */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
                <div className="md:col-span-2 bg-gradient-to-br from-mexred to-marigold text-cocoa rounded-3xl p-6 shadow-lg">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
                            <User className="w-5 h-5" />
                        </div>
                        <div>
                            <span className="text-xs uppercase tracking-widest opacity-80">Hola</span>
                            <div className="font-display text-2xl font-semibold">{user.name}</div>
                        </div>
                    </div>
                    <p className="text-cocoa/80">{user.email}</p>
                    {user.phone && <p className="text-cocoa/80">{user.phone}</p>}
                </div>
                <div className="bg-mexyellow rounded-3xl p-6 shadow-lg flex flex-col gap-2">
                    <div className="flex items-center gap-3">
                        <Award className="w-10 h-10 text-cocoa" />
                        <div>
                            <span className="text-xs uppercase tracking-widest text-cocoa/80">Puntos disponibles</span>
                            <div data-testid="user-points" className="font-display text-3xl font-bold text-cocoa">{points}</div>
                        </div>
                    </div>
                    {(user.points_pending || 0) > 0 && (
                        <div className="bg-white/40 rounded-2xl p-2 text-cocoa text-sm flex items-center gap-2 border border-cocoa/10">
                            ⏳ <strong>{user.points_pending}</strong> puntos pendientes (se liberan al aprobar tu pedido)
                        </div>
                    )}
                </div>
            </div>

            {/* Pending gift credit (devolución refund) */}
            {(user.pending_gift_credit || 0) > 0 && (
                <div className="mb-6 bg-gradient-to-r from-orange-100 to-mexyellow border-2 border-orange-400 rounded-3xl p-4 flex items-center gap-4 shadow-md">
                    <div className="text-4xl">🎁</div>
                    <div className="flex-1">
                        <div className="font-display text-lg font-semibold text-cocoa">¡Tienes {user.pending_gift_credit} regalo{user.pending_gift_credit > 1 ? "s" : ""} pendiente{user.pending_gift_credit > 1 ? "s" : ""} por reclamar!</div>
                        <div className="text-sm text-cocoa/80">Como compensación por tu devolución, te entregaremos un detalle especial en tu próximo pedido. Avísanos al hacer tu compra ✨</div>
                    </div>
                </div>
            )}

            {/* Pending reward banner */}
            {pendingReward && (
                <div className="mb-6 bg-gradient-to-r from-marigold/15 to-mexyellow/30 border-2 border-marigold rounded-3xl p-4 flex items-center gap-4 shadow-md">
                    {pendingReward.photo_base64 && (
                        <img src={pendingReward.photo_base64} alt={pendingReward.name} className="w-16 h-16 object-cover rounded-2xl" />
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="text-xs uppercase tracking-widest text-marigold font-bold flex items-center gap-1">
                            <Sparkles className="w-3.5 h-3.5" /> Canje preparado
                        </div>
                        <div className="font-display text-lg font-semibold text-cocoa truncate">{pendingReward.name}</div>
                        <div className="text-sm text-cocoa/80">
                            {pendingReward.points_cost} pts · Completa un pedido de $10.000 o más para confirmarlo.
                        </div>
                    </div>
                    <button
                        data-testid="clear-reward-btn"
                        onClick={() => { setPendingReward(null); toast.info("Canje cancelado"); }}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold text-cocoa hover:bg-white/50"
                    >
                        Quitar
                    </button>
                </div>
            )}

            {/* Cash redemption */}
            <section className="mb-8 bg-white border border-border rounded-3xl p-6 shadow-sm" data-testid="cash-redemption-section">
                <h2 className="font-display text-2xl font-semibold text-cocoa mb-2 flex items-center gap-2">
                    <Coins className="w-6 h-6 text-marigold" /> Canjear puntos por dinero
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                    1 punto equivale a <strong className="text-cocoa">$10 CLP</strong>. Este canje se puede hacer{" "}
                    <strong className="text-cocoa">una sola vez</strong>. Los puntos que acumules después solo podrán usarse
                    para canjear productos.
                </p>

                {user.cash_redeemed ? (
                    <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-2xl p-4">
                        <Check className="w-6 h-6 text-green-600 shrink-0" />
                        <div>
                            <div className="font-semibold text-green-900">Ya usaste tu canje por dinero ✨</div>
                            <div className="text-sm text-green-800">
                                A partir de ahora tus puntos sirven solo para canjear productos del catálogo.
                            </div>
                        </div>
                    </div>
                ) : points < 1 ? (
                    <div className="flex items-center gap-3 bg-muted/40 rounded-2xl p-4 text-muted-foreground">
                        <AlertCircle className="w-5 h-5 shrink-0" />
                        <span className="text-sm">Aún no tienes puntos disponibles. Haz tu primera compra para empezar a acumular.</span>
                    </div>
                ) : (
                    <div className="flex flex-wrap items-center justify-between gap-4 bg-marigold/10 rounded-2xl p-4">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-cocoa/60">Equivalente en dinero</div>
                            <div className="font-display text-3xl font-bold text-cocoa" data-testid="cash-equivalent">{formatCLP(cashValue)}</div>
                            <div className="text-xs text-cocoa/70">por tus {points} puntos</div>
                        </div>
                        <button
                            data-testid="redeem-cash-btn"
                            onClick={redeemCash}
                            disabled={redeeming}
                            className="bg-mexred hover:bg-mexred_hover disabled:bg-muted text-cocoa rounded-full px-6 py-3 font-semibold shadow-md flex items-center gap-2"
                        >
                            <Coins className="w-4 h-4" /> {redeeming ? "Procesando..." : `Canjear ${points} pts → ${formatCLP(cashValue)}`}
                        </button>
                    </div>
                )}
            </section>

            {/* Rewards catalog */}
            <section className="mb-8 bg-white border border-border rounded-3xl p-6 shadow-sm" data-testid="rewards-catalog-section">
                <h2 className="font-display text-2xl font-semibold text-cocoa mb-2 flex items-center gap-2">
                    <Gift className="w-6 h-6 text-mexred" /> Canjeables con puntos
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                    Elige un producto para canjear con tus puntos. Para confirmar el canje, tu pedido debe ser de{" "}
                    <strong className="text-cocoa">$10.000 CLP o más</strong>.
                </p>

                {rewards.length === 0 ? (
                    <p className="text-muted-foreground py-6 text-center">Aún no hay canjeables disponibles. ¡Pronto los tendremos!</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {rewards.map((r) => {
                            const canAfford = points >= r.points_cost;
                            const isSelected = pendingReward?.reward_id === r.id;
                            return (
                                <div
                                    key={r.id}
                                    data-testid={`reward-card-${r.id}`}
                                    className={`rounded-2xl border-2 p-4 flex flex-col transition-all ${
                                        isSelected
                                            ? "border-marigold bg-marigold/10 shadow-lg"
                                            : canAfford
                                                ? "border-border bg-white hover:shadow-lg hover:border-mexred"
                                                : "border-border bg-muted/30 opacity-70"
                                    }`}
                                >
                                    {r.photo_base64 ? (
                                        <img src={r.photo_base64} alt={r.name} className="w-full h-40 object-cover rounded-xl mb-3 bg-white" />
                                    ) : (
                                        <div className="w-full h-40 rounded-xl mb-3 bg-muted flex items-center justify-center">
                                            <Gift className="w-10 h-10 text-muted-foreground" />
                                        </div>
                                    )}
                                    <div className="flex-1">
                                        <h3 className="font-display font-semibold text-cocoa">{r.name}</h3>
                                        {r.description && (
                                            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{r.description}</p>
                                        )}
                                    </div>
                                    <div className="mt-3 flex items-center justify-between">
                                        <div>
                                            <div className="text-marigold font-bold text-lg">{r.points_cost} pts</div>
                                            {!canAfford && (
                                                <div className="text-xs text-muted-foreground">
                                                    Te faltan {r.points_cost - points} pts
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            data-testid={`add-reward-${r.id}`}
                                            onClick={() => selectReward(r)}
                                            disabled={!canAfford || isSelected}
                                            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                                                isSelected
                                                    ? "bg-marigold text-cocoa cursor-default"
                                                    : canAfford
                                                        ? "bg-mexred hover:bg-mexred_hover text-cocoa shadow"
                                                        : "bg-muted text-muted-foreground cursor-not-allowed"
                                            }`}
                                        >
                                            {isSelected ? "Seleccionado ✓" : canAfford ? "Canjear" : "Sin puntos"}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* Orders history */}
            <div className="bg-white border border-border rounded-3xl p-6 shadow-sm">
                <h2 className="font-display text-2xl font-semibold text-cocoa mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5" /> Mis pedidos
                </h2>
                {orders.length === 0 ? (
                    <p className="text-muted-foreground py-10 text-center">Aún no tienes pedidos. ¡Es hora de antojarse!</p>
                ) : (
                    <div className="space-y-3">
                        {orders.map((o) => (
                            <div key={o.id} data-testid={`order-${o.id}`} className="border border-border rounded-2xl p-4">
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                    <div>
                                        <div className="font-display font-semibold text-cocoa">Pedido #{o.id.slice(0, 8)}</div>
                                        <div className="text-xs text-muted-foreground">
                                            {new Date(o.created_at).toLocaleString("es-CL")}
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${STATUS_COLORS[o.status] || "bg-muted text-muted-foreground"}`}>
                                            {STATUS_LABELS[o.status] || o.status}
                                        </span>
                                        {o.shipping_info?.tracking_number && (
                                            <span className="text-[10px] text-blue-700">📦 Guía: {o.shipping_info.tracking_number}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-sm text-muted-foreground mb-2">
                                    {o.items.length} producto{o.items.length !== 1 ? "s" : ""} · +{o.points_earned} pts{" "}
                                    {o.points_status === "awarded" ? <span className="text-green-700">✅ liberados</span>
                                        : o.points_status === "revoked" ? <span className="text-red-600">❌ no se sumarán</span>
                                        : <span className="text-amber-700">⏳ pendientes (esperan aprobación)</span>}
                                </div>
                                {o.redeemed_reward && (
                                    <div className="inline-flex items-center gap-2 text-xs bg-marigold/20 text-cocoa rounded-full px-3 py-1 mb-2">
                                        <Gift className="w-3 h-3" /> Canjeaste: {o.redeemed_reward.name} ({o.redeemed_reward.points_cost} pts)
                                    </div>
                                )}
                                <div className="flex items-center justify-between">
                                    <span className="text-cocoa font-display font-bold text-lg">{formatCLP(o.total)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
